import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

export interface DoodleCanvasRef {
  getDrawingData: () => string;
  getMaskData: () => string;
  clearDrawing: () => void;
  clearMask: () => void;
  importImage: (imageUrl: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

interface DoodleCanvasProps {
  width?: number;
  height?: number;
  tool: 'brush' | 'eraser' | 'pan' | 'mask';
  brushSize: number;
  brushColor: string;
  onStrokeEnd?: () => void;
  onStrokeStart?: () => void;
  zoom: number;
  panOffset: { x: number; y: number };
  onPanChange?: (offset: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  className?: string;
  lightBackground?: boolean;
  cameraStream?: MediaStream | null;
}

const CANVAS_WIDTH = 1024;
const CANVAS_HEIGHT = 1024;

export const DoodleCanvas = forwardRef<DoodleCanvasRef, DoodleCanvasProps>(({
  tool,
  brushSize,
  brushColor,
  onStrokeEnd,
  onStrokeStart,
  zoom,
  panOffset,
  onPanChange,
  onZoomChange,
  className = '',
  lightBackground = false,
  cameraStream = null,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null);
  const [historyStack, setHistoryStack] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);

  // Initialize canvases
  useEffect(() => {
    const baseCanvas = baseCanvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;

    if (!baseCanvas || !drawingCanvas || !maskCanvas || !previewCanvas) return;

    // Set canvas dimensions
    [baseCanvas, drawingCanvas, maskCanvas, previewCanvas].forEach(canvas => {
      canvas.width = CANVAS_WIDTH;
      canvas.height = CANVAS_HEIGHT;
    });

    // Initialize base canvas with white background
    const baseCtx = baseCanvas.getContext('2d');
    if (baseCtx) {
      baseCtx.fillStyle = '#ffffff';
      baseCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }

    // Initialize mask canvas with black (no edits)
    const maskCtx = maskCanvas.getContext('2d');
    if (maskCtx) {
      maskCtx.fillStyle = '#000000';
      maskCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }

    // Save initial state
    saveToHistory();
  }, []);

  // Handle camera stream
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (cameraStream) {
      video.srcObject = cameraStream;
      video.play().catch(console.error);
    } else {
      video.srcObject = null;
    }
  }, [cameraStream]);

  const saveToHistory = useCallback(() => {
    const drawingCanvas = drawingCanvasRef.current;
    if (!drawingCanvas) return;

    const dataUrl = drawingCanvas.toDataURL();
    setHistoryStack(prev => {
      const newStack = prev.slice(0, historyIndex + 1);
      newStack.push(dataUrl);
      // Limit history to 50 entries
      if (newStack.length > 50) newStack.shift();
      return newStack;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  const getCanvasCoordinates = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const drawLine = useCallback((
    ctx: CanvasRenderingContext2D,
    from: { x: number; y: number },
    to: { x: number; y: number },
    size: number,
    color: string,
    isEraser: boolean = false
  ) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = size * 2;

    if (isEraser) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
    }

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    // Reset composite operation
    ctx.globalCompositeOperation = 'source-over';
  }, []);

  const drawOnMask = useCallback((
    from: { x: number; y: number },
    to: { x: number; y: number },
    size: number
  ) => {
    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    const ctx = maskCanvas.getContext('2d');
    if (!ctx) return;

    // Draw white on mask (white = areas to edit)
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = size * 2 + 20; // Add padding for smooth inpainting
    ctx.strokeStyle = '#ffffff';

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'pan') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      return;
    }

    const drawingCanvas = drawingCanvasRef.current;
    if (!drawingCanvas) return;

    const ctx = drawingCanvas.getContext('2d');
    if (!ctx) return;

    const coords = getCanvasCoordinates(e);
    setIsDrawing(true);
    setLastPoint(coords);
    onStrokeStart?.();

    // Draw initial dot
    if (tool === 'brush' || tool === 'mask') {
      ctx.fillStyle = tool === 'mask' ? 'rgba(255, 0, 0, 0.5)' : brushColor;
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, brushSize, 0, Math.PI * 2);
      ctx.fill();

      if (tool === 'mask') {
        drawOnMask(coords, coords, brushSize);
      }
    } else if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, brushSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }
  }, [tool, brushSize, brushColor, panOffset, getCanvasCoordinates, onStrokeStart, drawOnMask]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Update cursor preview
    const previewCanvas = previewCanvasRef.current;
    if (previewCanvas && tool !== 'pan') {
      const ctx = previewCanvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        const coords = getCanvasCoordinates(e);

        ctx.strokeStyle = tool === 'eraser' ? '#ff0000' : brushColor;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.arc(coords.x, coords.y, brushSize, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }

    if (isPanning && panStart && onPanChange) {
      onPanChange({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    if (!isDrawing || !lastPoint) return;

    const drawingCanvas = drawingCanvasRef.current;
    if (!drawingCanvas) return;

    const ctx = drawingCanvas.getContext('2d');
    if (!ctx) return;

    const coords = getCanvasCoordinates(e);

    if (tool === 'brush') {
      drawLine(ctx, lastPoint, coords, brushSize, brushColor);
      drawOnMask(lastPoint, coords, brushSize);
    } else if (tool === 'eraser') {
      drawLine(ctx, lastPoint, coords, brushSize, '#000', true);
    } else if (tool === 'mask') {
      // Draw semi-transparent red on visible canvas
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = brushSize * 2;
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      // Draw on actual mask
      drawOnMask(lastPoint, coords, brushSize);
    }

    setLastPoint(coords);
  }, [tool, brushSize, brushColor, isDrawing, lastPoint, isPanning, panStart, onPanChange, getCanvasCoordinates, drawLine, drawOnMask]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      setPanStart(null);
      return;
    }

    if (isDrawing) {
      setIsDrawing(false);
      setLastPoint(null);
      saveToHistory();
      onStrokeEnd?.();
    }
  }, [isDrawing, isPanning, saveToHistory, onStrokeEnd]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (!onZoomChange) return;

    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.25, Math.min(4, zoom + delta));
    onZoomChange(newZoom);
  }, [zoom, onZoomChange]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getDrawingData: () => {
      const baseCanvas = baseCanvasRef.current;
      const drawingCanvas = drawingCanvasRef.current;
      const video = videoRef.current;
      if (!baseCanvas || !drawingCanvas) return '';

      // Composite layers: video (if active) + base + drawing
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = CANVAS_WIDTH;
      compositeCanvas.height = CANVAS_HEIGHT;
      const ctx = compositeCanvas.getContext('2d');
      if (!ctx) return '';

      // If camera is active, draw video frame first
      if (cameraStream && video && video.readyState >= 2) {
        // Scale video to fit canvas while maintaining aspect ratio
        const videoAspect = video.videoWidth / video.videoHeight;
        const canvasAspect = CANVAS_WIDTH / CANVAS_HEIGHT;
        let drawWidth, drawHeight, drawX, drawY;

        if (videoAspect > canvasAspect) {
          drawHeight = CANVAS_HEIGHT;
          drawWidth = drawHeight * videoAspect;
          drawX = (CANVAS_WIDTH - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = CANVAS_WIDTH;
          drawHeight = drawWidth / videoAspect;
          drawX = 0;
          drawY = (CANVAS_HEIGHT - drawHeight) / 2;
        }

        ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
      } else {
        // No camera, use base canvas (white background)
        ctx.drawImage(baseCanvas, 0, 0);
      }

      // Draw user strokes on top
      ctx.drawImage(drawingCanvas, 0, 0);

      return compositeCanvas.toDataURL('image/png');
    },

    getMaskData: () => {
      const maskCanvas = maskCanvasRef.current;
      if (!maskCanvas) return '';
      return maskCanvas.toDataURL('image/png');
    },

    clearDrawing: () => {
      const drawingCanvas = drawingCanvasRef.current;
      if (!drawingCanvas) return;

      const ctx = drawingCanvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }
      saveToHistory();
    },

    clearMask: () => {
      const maskCanvas = maskCanvasRef.current;
      if (!maskCanvas) return;

      const ctx = maskCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      }
    },

    importImage: (imageUrl: string) => {
      const baseCanvas = baseCanvasRef.current;
      const drawingCanvas = drawingCanvasRef.current;
      if (!baseCanvas || !drawingCanvas) return;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Clear drawing layer
        const drawingCtx = drawingCanvas.getContext('2d');
        if (drawingCtx) {
          drawingCtx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        }

        // Draw imported image to base layer
        const baseCtx = baseCanvas.getContext('2d');
        if (baseCtx) {
          baseCtx.fillStyle = '#ffffff';
          baseCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

          // Scale to fit while maintaining aspect ratio
          const scale = Math.min(CANVAS_WIDTH / img.width, CANVAS_HEIGHT / img.height);
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (CANVAS_WIDTH - scaledWidth) / 2;
          const y = (CANVAS_HEIGHT - scaledHeight) / 2;

          baseCtx.drawImage(img, x, y, scaledWidth, scaledHeight);
        }

        saveToHistory();
      };
      img.src = imageUrl;
    },

    undo: () => {
      if (historyIndex <= 0) return;

      const newIndex = historyIndex - 1;
      const drawingCanvas = drawingCanvasRef.current;
      if (!drawingCanvas) return;

      const ctx = drawingCanvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.drawImage(img, 0, 0);
        setHistoryIndex(newIndex);
      };
      img.src = historyStack[newIndex];
    },

    redo: () => {
      if (historyIndex >= historyStack.length - 1) return;

      const newIndex = historyIndex + 1;
      const drawingCanvas = drawingCanvasRef.current;
      if (!drawingCanvas) return;

      const ctx = drawingCanvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.drawImage(img, 0, 0);
        setHistoryIndex(newIndex);
      };
      img.src = historyStack[newIndex];
    },

    canUndo: () => historyIndex > 0,
    canRedo: () => historyIndex < historyStack.length - 1,
  }), [historyIndex, historyStack, saveToHistory]);

  const getCursor = () => {
    switch (tool) {
      case 'pan': return isPanning ? 'grabbing' : 'grab';
      case 'brush':
      case 'eraser':
      case 'mask':
        return 'crosshair';
      default: return 'default';
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${lightBackground ? 'bg-neutral-100' : 'bg-graphite'} ${className}`}
      style={{ cursor: getCursor(), minHeight: 0 }}
    >
      {/* Canvas container with zoom/pan */}
      <div
        className="absolute inset-0 flex items-center justify-center z-0"
        style={{
          transform: `scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
          transformOrigin: 'center center',
        }}
      >
        {/* Checkerboard background for transparency (only in dark mode, no camera) */}
        {!lightBackground && !cameraStream && (
          <div
            className="absolute"
            style={{
              width: CANVAS_WIDTH,
              height: CANVAS_HEIGHT,
              backgroundImage: `
                linear-gradient(45deg, #1a1a1a 25%, transparent 25%),
                linear-gradient(-45deg, #1a1a1a 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #1a1a1a 75%),
                linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)
              `,
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
            }}
          />
        )}

        {/* Camera video feed (when camera is active) */}
        {cameraStream && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute object-cover"
            style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
          />
        )}

        {/* Base canvas (white bg or imported image) - hidden when camera active */}
        <canvas
          ref={baseCanvasRef}
          className="absolute"
          style={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            opacity: cameraStream ? 0 : 1,
          }}
        />

        {/* Drawing canvas (user strokes) */}
        <canvas
          ref={drawingCanvasRef}
          className="absolute"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />

        {/* Mask canvas (hidden, for inpainting) */}
        <canvas
          ref={maskCanvasRef}
          className="absolute hidden"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        />

        {/* Preview canvas (brush cursor) */}
        <canvas
          ref={previewCanvasRef}
          className="absolute pointer-events-none"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        />
      </div>

    </div>
  );
});

DoodleCanvas.displayName = 'DoodleCanvas';

export default DoodleCanvas;
